import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HeadsetIcon, LeafIcon } from 'lucide-react';
import type { Product, VariantSelection } from '../../types/product';
import { ProductGallery } from './ProductGallery';
import { VariantSelector } from './VariantSelector';
import { StarRating } from './StarRating';
import { StockLabel } from './StockLabel';
import { ProductPerks } from './ProductPerks';
import { StickyBuyBar } from './StickyBuyBar';
import { ProductTabs } from './ProductTabs';
import { RelatedProducts } from './RelatedProducts';
import { Breadcrumbs } from '../layout/Breadcrumbs';
import { useCart } from '../../contexts/CartContext';
import { useInView } from '../../hooks/useInView';
import { formatPrice } from '../../utils/currency';
import { defaultSelection, resolveVariant } from '../../utils/products';

/** Bruikbare kWh × ~300 cycli/jaar × € 0,25 gem. verschil tussen terugleveren en afnemen. */
function estimateSavings(capacityWh: number) {
  return Math.round(capacityWh / 1000 * 0.9 * 300 * 0.25);
}
export function SystemBuilderView({
  product
}: {
  product: Product;
}) {
  const {
    addItem
  } = useCart();
  const [selection, setSelection] = useState<VariantSelection>(() => defaultSelection(product));
  const {
    ref: summaryRef,
    inView
  } = useInView<HTMLDivElement>('-120px 0px 0px 0px');
  const v = resolveVariant(product, selection);
  const groups = product.variants ?? [];
  const savings = estimateSavings(v.capacityWh);
  const payback = savings > 0 ? (v.price / savings).toFixed(1).replace('.', ',') : '—';
  const add = () => addItem(product, selection);
  return <div className="mx-auto max-w-7xl px-4 pb-24 pt-5 sm:px-6 lg:px-8" data-id="element-523" data-mp="3m784v1n5e329">
      <Breadcrumbs items={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Thuisbatterijen',
      href: '/shop/home-batteries'
    }, {
      label: product.name
    }]} data-id="element-524" data-mp="3h8wwkvci40m5" />

      <div className="mt-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between" data-id="element-525" data-mp="ds6gtor4a979">
        <div data-id="element-526" data-mp="2824opahsb26q">
          <p className="text-sm font-medium text-muted" data-id="element-527" data-mp="281lgilhxrs5w">{product.brand} · Stel je systeem samen</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink sm:text-[32px]" data-id="element-528" data-mp="2czmyt47jfngb">{product.name}</h1>
        </div>
        <a href="#details" className="inline-flex items-center gap-2 text-sm" data-id="element-529" data-mp="1y95zbqegr7m2">
          <StarRating rating={product.rating} data-id="element-530" data-mp="238saluaqeds8" />
          <span className="text-muted hover:underline" data-id="element-531" data-mp="1patzqyvmczhh">
            {product.rating.toLocaleString('nl-NL')} · {product.reviews.toLocaleString('nl-NL')} reviews
          </span>
        </a>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-12 lg:gap-10" data-id="element-532" data-mp="1fcnzsmtdi3f9">
        {/* Configurator */}
        <div className="lg:col-span-8" data-id="element-533" data-mp="1kb900m8mkn3p">
          <div className="max-w-2xl" data-id="element-534" data-mp="1kcab2qpn59lf">
            <ProductGallery images={product.gallery} alt={product.name} activeImage={v.image} data-id="element-535" data-mp="1kdbzdwvo3sl3" />
          </div>

          <ol className="mt-10 space-y-10" data-id="element-536" data-mp="3epi8mq503zar">
            {groups.map((g, i) => <li key={g.id} className="grid gap-4 sm:grid-cols-[2.5rem_1fr]" data-id="element-537" data-mp="3en5vqre3of4k" data-mpi={i}>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-sm font-bold text-white" aria-hidden="true" data-id="element-538" data-mp="132g6lk0o6oi7">
                  {i + 1}
                </span>
                <div data-id="element-539" data-mp="181j9oyx3aknj">
                  <h2 className="text-lg font-bold tracking-tight text-ink" data-id="element-540" data-mp="18088n6v13r04">{g.name}</h2>
                  <div className="mt-3" data-id="element-541" data-mp="1lrv79dcg1bm2">
                    <VariantSelector group={g} value={selection[g.id]} onChange={id => setSelection(s => ({
                  ...s,
                  [g.id]: id
                }))} hideLegend={g.kind !== 'swatch'} data-id="element-542" data-mp="1lqtk4ai5kynz" />
                  </div>
                </div>
              </li>)}
          </ol>
        </div>

        {/* Sticky summary */}
        <aside className="lg:col-span-4" aria-label="Jouw systeem" data-id="element-543" data-mp="1qpob3zq9xtj6">
          <div ref={summaryRef} className="rounded-2xl border border-line bg-white p-5 lg:sticky lg:top-40" data-id="element-544" data-mp="1dm5vx0nwp2u">
            <h2 className="text-base font-bold text-ink" data-id="element-545" data-mp="3skoxgpkewxdl">Jouw systeem</h2>
            <dl className="mt-4 divide-y divide-line text-sm" data-id="element-546" data-mp="2jmyu8ps0lebd">
              {groups.map(g => {
              const opt = g.options.find(o => o.id === selection[g.id]) ?? g.options[0];
              return <div key={g.id} className="flex items-start justify-between gap-3 py-2.5" data-id="element-547" data-mp="2tlobqhg00wr3">
                    <dt className="text-muted" data-id="element-548" data-mp="2xknhg49k0xjl">{g.name}</dt>
                    <dd className="text-right" data-id="element-549" data-mp="20vxzyf1ua6y5">
                      <span className="block font-medium text-ink" data-id="element-550" data-mp="20x93cdrdhcoq">{opt.label}</span>
                      {opt.priceDelta > 0 && <span className="text-xs text-muted" data-id="element-551" data-mp="25v0tp3cdcqqt">+{formatPrice(opt.priceDelta)}</span>}
                    </dd>
                  </div>;
            })}
            </dl>

            <div className="mt-3 border-t border-line pt-4" data-id="element-552" data-mp="2eonoaq4ubnyf">
              <div className="flex items-baseline justify-between" data-id="element-553" data-mp="1i5zr3wq7w9tg">
                <span className="text-sm font-semibold text-ink" data-id="element-554" data-mp="1m6s14s6wyiqs">Totaal</span>
                <span className="text-right" data-id="element-555" data-mp="1m4p1dgfylb8v">
                  <AnimatePresence mode="popLayout" initial={false} data-id="element-556" data-mp="1ensg6i3t0j5b">
                    <motion.span key={v.price} initial={{
                    opacity: 0,
                    y: 6
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -6
                  }} transition={{
                    duration: 0.16,
                    ease: [0.23, 1, 0.32, 1]
                  }} className="block text-[28px] font-bold leading-none tracking-tight text-ink" data-id="element-557" data-mp="1ekwi8tryhugv">
                      {formatPrice(v.price)}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </div>
              {v.capacityWh > 0 && <p className="mt-1.5 text-right text-xs font-semibold text-ink" data-id="element-558" data-mp="hxjkr8xwntl9">
                  <span className="rounded bg-volt/40 px-1.5 py-0.5" data-id="element-559" data-mp="cxx5ktyio39t">{formatPrice(Math.round(v.price / (v.capacityWh / 1000)))} per kWh</span>
                </p>}
              {v.compareAt && <p className="mt-1 text-right text-xs" data-id="element-560" data-mp="3ugvnqr9jz1ow">
                  <span className="text-muted line-through" data-id="element-561" data-mp="3ufatl2r5hkrk">{formatPrice(v.compareAt)}</span>{' '}
                  <span className="font-semibold text-sale" data-id="element-562" data-mp="3bm6bnzmjvqw">Je bespaart {formatPrice(v.compareAt - v.price)}</span>
                </p>}
            </div>

            <div className="mt-4 flex items-start gap-3 rounded-xl bg-surface p-3.5" data-id="element-563" data-mp="3pikfum2tj19b">
              <LeafIcon className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" data-id="element-564" data-mp="h1674o9naixk" />
              <p className="text-xs leading-relaxed text-ink" data-id="element-565" data-mp="m1c0q9zh6fia">
                Geschat <span className="font-bold" data-id="element-566" data-mp="m2n2y5d9vf7a">{formatPrice(savings)} per jaar</span> besparing met{' '}
                {(v.capacityWh / 1000).toLocaleString('nl-NL')} kWh · terugverdiend in ~{payback} jaar
              </p>
            </div>

            <button type="button" onClick={add} className="mt-4 h-12 w-full rounded-lg bg-volt text-sm font-bold text-ink transition-[background-color,transform] duration-150 hover:bg-volt-hover active:scale-[0.98]" data-id="element-567" data-mp="100bmn9vlsgar">
              Systeem in winkelwagen
            </button>
            <div className="mt-3" data-id="element-568" data-mp="2ki2ysgszl2d7">
              <StockLabel status={product.stock} long data-id="element-569" data-mp="2z7inlr8yixq9" />
            </div>
            <a href="tel:+31851234567" className="mt-4 flex items-center gap-2 text-xs font-medium text-ink hover:underline" data-id="element-570" data-mp="2z6phmshtoc96">
              <HeadsetIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-571" data-mp="26itmvtbtcuf1" />
              Twijfel je? Onze technici checken je situatie gratis
            </a>
            <div className="mt-5" data-id="element-572" data-mp="21lbbzxe8k54e">
              <ProductPerks data-id="element-573" data-mp="2fjjeeku1jymy" />
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-14" data-id="element-574" data-mp="2fh6nhi6b2dxr">
        <ProductTabs product={product} data-id="element-575" data-mp="2bheljobf361d" />
      </div>
      <RelatedProducts product={product} data-id="element-576" data-mp="2bk0spmuebjj2" />

      <div className="lg:hidden" data-id="element-577" data-mp="3qy58wzubqn5q">
        <StickyBuyBar visible={!inView} image={v.image} name={product.name} options={v.labels} price={v.price} compareAt={v.compareAt} ctaLabel="In winkelwagen" onAdd={add} data-id="element-578" data-mp="3qx3m5y3y4c86" />
      </div>
    </div>;
}